import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@firecms/ui";

import { useEventIndividualLeaderboard } from "../hooks/useEventLeaderboard";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";
import { RankingRefreshBar } from "./RankingRefreshBar";
import SearchBar from "./SearchBar";
import {
  statsMuted,
  statsPageTitle,
} from "./statsViewTheme";

type IndividualRankingViewProps = {
  eventId: EventCollectionId;
};

export function IndividualRankingView({ eventId }: IndividualRankingViewProps) {
  const { t, locale } = useAdminTranslation();
  const { rows, meta, loading, error } = useEventIndividualLeaderboard(eventId);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [eventId.accountId, eventId.eventId]);

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return rows;
    }
    const q = search.toLowerCase();
    return rows.filter((row) => {
      const team = row.teamNumber != null ? String(row.teamNumber) : "";
      return (
        row.displayName.toLowerCase().includes(q) ||
        team.includes(q) ||
        row.userId.toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className={statsPageTitle}>
              {t("eventRanking.individualTitle")}
            </h1>
            <p className={`mt-2 text-sm ${statsMuted}`}>
              {t("eventRanking.individualDescription")}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <RankingRefreshBar
            eventId={eventId}
            ranksUpdatedAt={meta?.ranksUpdatedAt ?? 0}
            locale={locale}
            t={t}
          />
        </div>

        <div className="mt-6">
          <SearchBar
            onSearch={setSearch}
            placeholder={t("eventRanking.searchIndividual")}
          />
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        ) : null}

        {!loading && error ? (
          <p className={`mt-6 text-sm ${statsMuted}`}>{error}</p>
        ) : null}

        {!loading && !error && filtered.length === 0 ? (
          <p className={`mt-6 text-sm ${statsMuted}`}>
            {t("eventRanking.emptyIndividual")}
          </p>
        ) : null}

        {!loading && !error && filtered.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-surface-100 dark:bg-surface-800">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colRank")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colName")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colTeam")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colScore")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colUpdated")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.userId}
                    className="border-t border-surface-200 dark:border-surface-700"
                  >
                    <td className="px-3 py-2 tabular-nums">
                      {row.rank != null && row.rank > 0 ? row.rank : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {row.displayName || row.userId}
                      </div>
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.teamNumber != null ? row.teamNumber : "—"}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.individualScore}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.updatedAt > 0
                        ? new Date(row.updatedAt).toLocaleString(locale)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
